import { useContext } from "react";

import { useTranslation } from "react-i18next";

import { FileDownloadIcon } from "@dotnet/telematics-design-system";

import TctWebIcon from "assets/customIcons/navigation/TctWebIcon";
import AuthContext from "context/auth/authContext";
import {
    downloadFile,
    getEnvironment,
    isInternalOrPremiumLevel
} from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { usePermissions } from "shared/hooks/usePermissions";
import { useTct } from "shared/hooks/useTct";
import { RenderConditionOptions } from "shared/types/interfaces";

import { TITLES } from "../constants";

const useConfigChangesInlineActions = (isOverview: boolean) => {
    const { user } = useContext(AuthContext);

    const { t } = useTranslation();
    const { isFotaGlobalEnv } = getEnvironment();
    const { getData, handleResponse } = useApi();
    const { editConfigurationOnline } = useTct();

    const {
        isDownloadFileNotAllowed,
        isEditInTctWebNotAllowed,
        PERMISSIONS,
        getNoPermissionTooltipTitle
    } = usePermissions();

    const getDisabledEditTctWebText = () => {
        if (isEditInTctWebNotAllowed || isDownloadFileNotAllowed) {
            return getNoPermissionTooltipTitle([
                PERMISSIONS.FileTctWebAccess,
                PERMISSIONS.FileDownload
            ]);
        }

        if (!isInternalOrPremiumLevel(user?.company_level)) {
            return t(`General##edit in tct web disabled`);
        }

        return undefined;
    };

    const getFileId = (data: any) => {
        const mainData = isOverview ? data.configurationChange : data;

        return mainData?.task?.file?.id;
    };

    const hasTctWebEditorSupport = (data: any) => {
        if (!data) return false;

        if (isOverview) {
            return Boolean(
                data.configurationChange?.task?.file?.web_editor_support
            );
        }

        return Boolean(data.task?.file?.web_editor_support);
    };

    const isDownloadable = (data: any) => {
        if (!data) return false;

        if (isOverview) {
            return Boolean(
                data.configurationChange?.task?.file?.is_downloadable
            );
        }

        return Boolean(data.task?.file?.is_downloadable);
    };

    const inlineActions = [
        {
            title: TITLES.OpenInTct,
            icon: TctWebIcon,
            getDisabledText: () => getDisabledEditTctWebText(),
            clickAction: (data: any) =>
                editConfigurationOnline(getFileId(data)),
            renderConditions: ({ data }: RenderConditionOptions) =>
                isFotaGlobalEnv && hasTctWebEditorSupport(data)
        },
        {
            title: TITLES.DownloadFile,
            hasLoader: true,
            icon: FileDownloadIcon,
            getDisabledText: () =>
                getNoPermissionTooltipTitle(PERMISSIONS.FileDownload),
            clickAction: (data: any, removeLoading: () => void) =>
                downloadFile({
                    id: getFileId(data),
                    getData,
                    handleResponse,
                    removeLoading
                }),
            renderConditions: ({ data }: RenderConditionOptions) =>
                isDownloadable(data)
        }
    ];

    return { inlineActions };
};

export { useConfigChangesInlineActions };
